import { useFeatureAccess } from "@/app/hooks/useFeatureAccess";
import { IBookingSlotsFilterBody } from "@/api_framework/api_modals/Bookings";
import {
  IPatientWaitingListData,
  ITeleconsultationUserDoctorMeetDetailsResponsePostBody,
} from "@/api_framework/api_modals/Patients";
import { IBookingCancelationModalState } from "@/app/components/routes/modals/Cancelation";
import RBACFlag from "@/app/components/core/RBACFlag";
import { usePatientWaitingPetReadyAPI } from "@/app/hooks/api_hooks/PWL/usePatientWaitingPetReadyAPI";
import { useModalActions } from "@/app/hooks/ui_hooks/useModalActions";
import { useArrayConstants } from "@/app/hooks/useArrayConstants";
import { useRouter } from "@/app/hooks/useRouter";
import { useStringConstants } from "@/app/hooks/useStringConstants";
import { useUtils } from "@/app/hooks/useUtils";
import { ROUTES } from "@/app/routes";
import CallClientIcon from "@/assets/callclient.svg";
import ConnectStaffIcon from "@/assets/connect_staff.svg";
import profileIcon from "@/assets/images/profile_icon.svg";
import FillFormIcon from "@/assets/fill_form.svg";
import { useAppDispatch, useAppSelector } from "@/app_redux/hooks/root_hook";
import {
  setRescheduleFlag,
  setRescheduledBooking,
  setSlotBookingFilters,
  setSlotsList,
} from "@/app_redux/reducers/slices/auth/slots_slice";
import { Menu, Transition } from "@headlessui/react";
import { EllipsisVerticalIcon } from "@heroicons/react/24/outline";
import { format } from "date-fns";
import { Fragment, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router";
import Teleconsultation from "@components/routes/auth_routes/CustomerProfile/CustomerDetails/CustomerInformation/PhoneDropdown/teleconsultation";
import { IThreeDotModalProps } from "../../interface";
import { useClinicManagerPhoneAPI } from "@/app/hooks/api_hooks/customer_profile/useClinicManagerPhoneAPI";
import { useTeleconsultationUserDoctorMeetDetailsAPI } from "@/app/hooks/api_hooks/PWL/useTeleconsultationUserDoctorMeetDetailsAPI";
import { useDoctorUserMeetDetailsMsgAPI } from "@/app/hooks/api_hooks/PWL/useDoctorUserMeetDetailsMsgAPI";
import ClipboardComponent from "@/app/components/ui/Clipboard";
import { FaWhatsapp } from "react-icons/fa6";

import useRefetchPWL from "@/app/hooks/api_hooks/process/useReFetchPWL";
import useRefetchPWLStats from "@/app/hooks/api_hooks/process/useReFetchPWLStats";
import ChangeDoctorModal from "../ChangeDoctorModal";
import {
  shouldAutoStartVoiceFromPWL,
  shouldVerifyVoiceFromPWL,
  VOICE_PARAM_START,
  VOICE_PARAM_VERIFY,
} from "@/app/utils/voiceAutoStart";

const ThreeDotModalComponent: React.FC<IThreeDotModalProps> = ({ bookingDetails, variant = "DEFAULT" }) => {
  const navigate = useNavigate();
  const { slotStatusAvailable } = useStringConstants();
  const { getRouteKey } = useRouter();
  const { setVisible, setModalData } = useModalActions("APPOINTMENT_CANCELATION");
  const { setVisible: openUploadJobCardModal, setModalData: setUploadJobCardModalData } = useModalActions(
    "CERTIFICATE_DOCUMENT_UPLOAD_MODAL",
  );
  const { setVisible: setReschedule, setModalData: setRescheduleData } = useModalActions("APPOINTMENT_RESCHEDULE");
  // Front desk overbooking users cannot reschedule
  const isFrontDeskOverbooking = useFeatureAccess("FRONT_DESK_OVERBOOKING");
  const bookingStatus: string = bookingDetails?.booking_status?.toLowerCase();
  const { rescheduleAndCancelAllowedFor } = useArrayConstants();
  const sendWhatsAppToUser = usePatientWaitingPetReadyAPI();
  const fetchClinicmanagersMobile = useClinicManagerPhoneAPI();
  const { classNames } = useUtils();
  const dispatch = useAppDispatch();
  const [open, setOpen] = useState(false);
  const fetchMeetDetails = useTeleconsultationUserDoctorMeetDetailsAPI();
  const [meetDetailsLoading, setMeetDetailsLoading] = useState(false);
  const [userMeetUrl, setUserMeetUrl] = useState<string | null>(null);
  const [doctorMeetUrl, setDoctorMeetUrl] = useState<string | null>(null);
  const { execute: sendMeetDetailsMsg } = useDoctorUserMeetDetailsMsgAPI();
  const isAtHomeVariant = variant === "AT_HOME";
  const [openChangeDoctorModal, setOpenChangeDoctorModal] = useState(false);

  // Same voice-recording gate the PWL name link uses. PWL rows only carry a
  // service_id, so the service type is resolved from the org services list.
  const loggedInUserEmail = useAppSelector((state) => state.auth?.user?.email);
  const loggedInUserRole = useAppSelector((state) => state.auth?.userPermissions?.role_name);
  const orgServices = useAppSelector((state) => state.staticAPI?.org_services);
  const getQualifiesForVoice = () => {
    // PWL rows carry only service_id; org services hold the service_type_id at
    // runtime even though the type doesn't declare it, so read it via `any`.
    const serviceTypeId = ((orgServices ?? []) as any[]).find(
      (service) => String(service?.service_id) === String(bookingDetails?.service_id),
    )?.service_type_id as string | undefined;
    return (
      shouldAutoStartVoiceFromPWL({
        email: loggedInUserEmail,
        roleName: loggedInUserRole,
        booking: bookingDetails,
        serviceTypeId,
      }) &&
      !!bookingDetails?.user_id &&
      !!bookingDetails?.pet_id
    );
  };

  const handelSendToDoctor = async () => {
    setMeetDetailsLoading(true);
    try {
      const response = await fetchMeetDetails(bookingDetails.booking_id);
      if (response?.user_meet_url) setUserMeetUrl(response.user_meet_url);
      if (response?.doctor_meet_url) setDoctorMeetUrl(response.doctor_meet_url);
    } finally {
      setMeetDetailsLoading(false);
    }
  };
  const handleSendNotification = async (target: "user" | "doctor") => {
    if (!bookingDetails?.booking_id) {
      toast.error("Booking ID not found");
      return;
    }
    const payload: ITeleconsultationUserDoctorMeetDetailsResponsePostBody = {
      booking_id: bookingDetails.booking_id,
      send_msg_to: target,
    };
    await sendMeetDetailsMsg(payload);
  };
  const handleRowSelect = () => {
    const userId = bookingDetails?.user_id;
    const petId = bookingDetails?.pet_id;
    toast.success("User and Pet Successfully selected");
    const base = ROUTES.ROOT.children.CUSTOMER_PROFILE.children.DETAILS_CUSTOMER.url;
    // Doctors always land on the EMR tab; everyone else keeps the appointments
    // view. `?voice=1` (only when voice-qualifying) makes the tab auto-start the
    // floating recorder (same flow as the name link); `?voice=check` makes it
    // re-read the live booking status first, because the PWL row saying "not
    // attending" may simply be stale.
    const isDoctorRole = loggedInUserRole?.trim().toLowerCase() === "doctor";
    if (isDoctorRole) {
      const qualifiesForVoice = getQualifiesForVoice();
      const needsVoiceVerification =
        !qualifiesForVoice &&
        !!userId &&
        !!petId &&
        shouldVerifyVoiceFromPWL({
          email: loggedInUserEmail,
          roleName: loggedInUserRole,
          booking: bookingDetails,
        });
      const voiceQuery = qualifiesForVoice
        ? `?voice=${VOICE_PARAM_START}`
        : needsVoiceVerification
          ? `?voice=${VOICE_PARAM_VERIFY}`
          : "";
      navigate(`${base}/EMR/${userId}/${petId}${voiceQuery}`);
      return;
    }
    navigate(`${base}/appointments/${userId}/${petId}`);
  };
  const handleRowSelectFrom = () => {
    const userId = bookingDetails?.user_id;
    const petId = bookingDetails?.pet_id;
    toast.success("User and Pet Successfully selected");
    navigate(`${ROUTES.ROOT.children.CUSTOMER_PROFILE.children.DETAILS_CUSTOMER.url}/form-tab/${userId}/${petId}`);
  };
  const handleOpenCommunicationModal = () => {
    setOpen(true);
    fetchClinicmanagersMobile();
  };
  const reconstructBookingFilters = (
    bookingDetails: IPatientWaitingListData,
    rescheduled = false,
  ): IBookingSlotsFilterBody => {
    return {
      booking_date: rescheduled ? bookingDetails?.booking_start_date : format(new Date(Date.now()), "yyyy-MM-dd"),
      clinic_ids: [],
      location_details: {
        latitude: "",
        longitude: "",
      },
      pet_species_id: bookingDetails?.pet_details?.pet_species_type_id,
      service_id: bookingDetails?.service_id,
      staff_ids: [],
      sub_service_id: bookingDetails?.subservice_id,
      city_id: "",
      slot_status: slotStatusAvailable,
    };
  };
  const handleModifyAppointment = () => {
    dispatch(
      setSlotsList({
        data: [],
      }),
    );
    const filters = reconstructBookingFilters(bookingDetails, true);
    dispatch(
      setSlotBookingFilters({
        filters: filters,
      }),
    );
    dispatch(setRescheduleFlag(true));
    dispatch(
      setRescheduledBooking({
        data: bookingDetails as any,
      }),
    );
    if (bookingDetails?.payment_details?.payment_status) {
      setReschedule();
      setRescheduleData({
        userId: bookingDetails?.user_details?.id,
        petId: bookingDetails?.pet_details?.id,
        bookingId: bookingDetails?.booking_id,
        price: bookingDetails?.payment_details?.amount,
        isRefunded: bookingDetails?.payment_details?.is_refundable,
      });
    } else {
      navigate(
        `${getRouteKey("AVAILABLE_SLOTS", "url")}/${bookingDetails?.user_id}/${bookingDetails?.pet_id}?type=reschedule&rbi=${bookingDetails?.booking_id}`,
      );
    }
  };
  const shouldCancelAndModifyVisible = (): boolean => {
    if (rescheduleAndCancelAllowedFor?.includes(bookingStatus?.toLowerCase())) {
      return true;
    }
    return false;
  };
  const handelSentToGupshup = () => {
    sendWhatsAppToUser({ user_id: bookingDetails?.user_id });
  };

  const refetchPWL = useRefetchPWL();
  const refetchPWLStats = useRefetchPWLStats();

  const fetchAppointmentData = () => {
    refetchPWLStats().then(() => {
      refetchPWL();
      setOpen(false);
    });
  };

  const handleUploadJobCard = () => {
    setUploadJobCardModalData({
      booking_id: bookingDetails?.booking_id,
      user_details: bookingDetails?.user_details,
      pet_details: bookingDetails?.pet_details,
      clinic_details: bookingDetails?.clinic_details,
      document_types: ["Job Card"],
    });
    openUploadJobCardModal();
  };
  return (
    <>
      <div className="z-50">
        <Menu as="div" className="relative inline-block text-left z-10">
          <div>
            <Menu.Button className="group inline-flex justify-center text-sm text-gray-700 hover:text-gray-900">
              <EllipsisVerticalIcon
                className="inline w-6 h-6"
                onClick={() => {
                  if (!isAtHomeVariant) {
                    handelSendToDoctor();
                  }
                }}
                color="bg-[rgba(104,113,131,1)]"
              />
              {/* <EllipsisVerticalIcon className="inline w-4 h-4" /> */}
            </Menu.Button>
          </div>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items
              className={`
                  absolute right-[20px] -top-[16px] z-50
                  flex flex-col items-start
                  p-[16px_20px] 
                  rounded-[10px]
                  bg-[#FBFBFB]
                  shadow-[0_4px_8px_rgba(0,0,0,0.05)]
                  focus:outline-none
                `}
            >
              <div className="flex flex-col gap-[15px] w-full">
                {isAtHomeVariant ? (
                  <>
                    <a
                      href={"#"}
                      onClick={(e) => {
                        e.preventDefault();
                        handleOpenCommunicationModal();
                      }}
                      className={classNames(
                        "text-gray-600 flex justify-start items-center px-0 py-0 text-[0.875rem] cursor-pointer hover:bg-gray-200",
                      )}
                    >
                      <img src={CallClientIcon} alt="Call Client" className="w-4 h-4 shrink-0" />
                      <span className="ml-2">Call Client</span>
                    </a>
                    <a
                      href={"#"}
                      onClick={(e) => {
                        e.preventDefault();
                        handleOpenCommunicationModal();
                      }}
                      className={classNames(
                        "text-gray-600 flex justify-start items-center px-0 py-0 text-[0.875rem] cursor-pointer hover:bg-gray-200",
                      )}
                    >
                      <img src={ConnectStaffIcon} alt="Connect Staff" className="w-4 h-4 shrink-0" />
                      <span className="ml-2">Connect Staff</span>
                    </a>
                    <a
                      href={"#"}
                      onClick={(e) => {
                        e.preventDefault();
                        handleRowSelect();
                      }}
                      className={classNames(
                        "text-gray-600 flex justify-start items-center px-0 py-0 text-[0.875rem] cursor-pointer hover:bg-gray-200",
                      )}
                    >
                      <img src={profileIcon} alt="Connect Staff" className="w-4 h-4 shrink-0" />
                      <span className="ml-2">Customer Profile</span>
                    </a>
                    <a
                      href={"#"}
                      onClick={(e) => {
                        e.preventDefault();
                        handleRowSelectFrom();
                      }}
                      className={classNames(
                        "text-gray-600 flex justify-start items-center px-0 py-0 text-[0.875rem] cursor-pointer hover:bg-gray-200",
                      )}
                    >
                      <img src={FillFormIcon} alt="Fill Form" className="w-4 h-4 shrink-0" />
                      <span className="ml-2">Fill Form</span>
                    </a>
                    <RBACFlag actionId="AVAILABLE_SLOTS_CANCELATION">
                      {shouldCancelAndModifyVisible() && (
                        <a
                          href={"#"}
                          onClick={() => {
                            setVisible();
                            setModalData({
                              bookingDetails: bookingDetails,
                              from: "AT_HOME_SERVICES",
                            } as IBookingCancelationModalState);
                          }}
                          className={classNames(
                            "text-gray-600 flex justify-start items-center px-0 py-0 text-[0.875rem] cursor-pointer hover:bg-gray-200",
                          )}
                        >
                          <span className="ml-2 text-red-500">Cancel Appointment</span>
                        </a>
                      )}
                    </RBACFlag>
                  </>
                ) : (
                  <>
                    <a
                      href={"#"}
                      onClick={() => {
                        setOpen(true);
                        fetchClinicmanagersMobile();
                      }}
                      className={classNames(
                        "text-gray-600 flex justify-start items-center px-0 py-0 text-[0.875rem] cursor-pointer hover:bg-gray-200",
                      )}
                    >
                      <span className="ml-2">Call Client</span>
                    </a>
                    {bookingDetails?.service_id === "d10441ad-4549-11ed-af80-d1c5db9bbce2" && (
                      <a
                        href={"#"}
                        onClick={handelSentToGupshup}
                        className={classNames(
                          "text-gray-600 flex justify-start items-center px-0 py-0 text-[0.875rem] cursor-pointer hover:bg-gray-200",
                        )}
                      >
                        <span className="ml-2">Pet Ready</span>
                      </a>
                    )}
                    <a
                      href={"#"}
                      onClick={handleRowSelect}
                      className={classNames(
                        "text-gray-600 flex justify-start items-center px-0 py-0 text-[0.875rem] cursor-pointer hover:bg-gray-200",
                      )}
                    >
                      <span className="ml-2">View Customer Profile</span>
                    </a>
                    <a
                      href={"#"}
                      onClick={handleRowSelectFrom}
                      className={classNames(
                        "text-gray-600 flex justify-start items-center px-0 py-0 text-[0.875rem] cursor-pointer hover:bg-gray-200",
                      )}
                    >
                      <span className="ml-2">Fill Form</span>
                    </a>
                    <a
                      href={"#"}
                      onClick={(e) => {
                        e.preventDefault();
                        handleUploadJobCard();
                      }}
                      className={classNames(
                        "text-gray-600 flex justify-start items-center px-0 py-0 text-[0.875rem] cursor-pointer hover:bg-gray-200",
                      )}
                    >
                      <span className="ml-2">Upload Job Card</span>
                    </a>
                    <RBACFlag actionId="AVAILABLE_SLOTS_RESCHEDULE">
                      {shouldCancelAndModifyVisible() && !bookingDetails?.update_pg && !isFrontDeskOverbooking && (
                        <a
                          href={"#"}
                          onClick={handleModifyAppointment}
                          className={classNames(
                            "text-gray-600 flex justify-start items-center px-0 py-0 text-[0.875rem] cursor-pointer hover:bg-gray-200",
                          )}
                        >
                          <span className="ml-2">Modify Appointment</span>
                        </a>
                      )}
                    </RBACFlag>

                    {bookingDetails?.update_pg && (
                      <>
                        <RBACFlag actionId="AVAILABLE_SLOTS_RESCHEDULE">
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              setOpenChangeDoctorModal(true);
                            }}
                            className={classNames(
                              "text-gray-600 flex justify-start items-center px-0 py-0 text-[0.875rem] cursor-pointer hover:bg-gray-200",
                            )}
                          >
                            <span className="ml-2">Change Doctor</span>
                          </a>
                        </RBACFlag>

                        {/* <RBACFlag actionId="AVAILABLE_SLOTS_CANCELATION">
                          <a
                            href="#"
                            onClick={() => {
                              setVisible();
                              setModalData({
                                bookingDetails: bookingDetails,
                                from: "PATIENT_WAITING_LIST",
                              } as IBookingCancelationModalState);
                            }}
                            className={classNames(
                              "text-gray-600 flex justify-start items-center px-0 py-0 text-[0.875rem] cursor-pointer hover:bg-gray-200",
                            )}
                          >
                            <span className="ml-2 text-red-500">PG Not Done</span>
                          </a>
                        </RBACFlag> */}
                      </>
                    )}

                    {bookingStatus !== "cancelled" &&
                      bookingDetails?.service_id === "827ad658-e209-47db-91d8-56bb8c3097a4" &&
                      !meetDetailsLoading &&
                      userMeetUrl && (
                        <a
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            handleSendNotification("user");
                          }}
                          className={classNames(
                            "text-gray-600 flex justify-start items-center px-0 py-0 text-[0.875rem] cursor-pointer hover:bg-gray-200",
                          )}
                        >
                          <FaWhatsapp className="w-4 h-4 text-green-600 ml-2" />
                          <span className="ml-1">Send Notification to User</span>
                          <ClipboardComponent
                            className="ml-1"
                            tooltipText={`Copy Url`}
                            text={`${userMeetUrl}`}
                            copyId={`copy-user-code:${userMeetUrl}`}
                          />
                        </a>
                      )}
                    {bookingStatus !== "cancelled" &&
                      bookingDetails?.service_id === "827ad658-e209-47db-91d8-56bb8c3097a4" &&
                      !meetDetailsLoading &&
                      doctorMeetUrl && (
                        <a
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            handleSendNotification("doctor");
                          }}
                          className={classNames(
                            "text-gray-600 flex justify-start items-center px-0 py-0 text-[0.875rem] cursor-pointer hover:bg-gray-200",
                          )}
                        >
                          <FaWhatsapp className="w-4 h-4 text-green-600 ml-2" />
                          <span className="ml-1">Send Notification to Doctor</span>
                          <ClipboardComponent
                            className="ml-1"
                            tooltipText={`Copy Url`}
                            text={`${doctorMeetUrl}`}
                            copyId={`copy-user-code:${doctorMeetUrl}`}
                          />
                        </a>
                      )}
                    <RBACFlag actionId="AVAILABLE_SLOTS_CANCELATION">
                      {shouldCancelAndModifyVisible() && (
                        <a
                          href={"#"}
                          onClick={() => {
                            setVisible();
                            setModalData({
                              bookingDetails: bookingDetails,
                              from: "PATIENT_WAITING_LIST",
                            } as IBookingCancelationModalState);
                          }}
                          className={classNames(
                            "text-gray-600 flex justify-start items-center px-0 py-0 text-[0.875rem] cursor-pointer hover:bg-gray-200",
                          )}
                        >
                          <span className="ml-2  text-red-500">Cancel Appointment</span>
                        </a>
                      )}
                    </RBACFlag>
                  </>
                )}
              </div>
              {open && (
                <Teleconsultation
                  setOpen={setOpen}
                  selected={bookingDetails?.user_details?.mobile}
                  customerId={bookingDetails?.user_id}
                />
              )}
            </Menu.Items>
          </Transition>
        </Menu>
      </div>

      {openChangeDoctorModal && (
        <ChangeDoctorModal
          open={openChangeDoctorModal}
          setOpen={setOpenChangeDoctorModal}
          bookingDetails={bookingDetails}
          onSuccess={fetchAppointmentData}
        />
      )}

    </>
  );
};
export default ThreeDotModalComponent;
